import React, { useState, useEffect } from 'react';
import { Languages, Save, Search, Plus } from 'lucide-react';
import { api } from '../../api/client';
import { showToast } from '../../components/common/Toast';

export default function AdminTranslationsPage() {
  const [translations, setTranslations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  // Grouped by key: { [key]: { uz: '', ru: '', en: '', group: '' } }
  const [dict, setDict] = useState({});

  const loadTranslations = () => {
    setLoading(true);
    api.get('/cms/translations')
      .then((res) => {
        if (res?.success) {
          setTranslations(res.translations || []);
          const grouped = {};
          (res.translations || []).forEach((t) => {
            if (!grouped[t.key]) {
              grouped[t.key] = { uz: '', ru: '', en: '', group: t.group || 'common' };
            }
            grouped[t.key][t.locale] = t.value;
          });
          setDict(grouped);
        }
      })
      .catch((err) => showToast(err.message, 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadTranslations();
  }, []);

  const handleValueChange = (key, locale, val) => {
    setDict((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [locale]: val,
      },
    }));
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const items = [];
      Object.entries(dict).forEach(([key, values]) => {
        if (values.uz !== undefined) items.push({ key, locale: 'uz', value: values.uz, group: values.group });
        if (values.ru !== undefined) items.push({ key, locale: 'ru', value: values.ru, group: values.group });
        if (values.en !== undefined) items.push({ key, locale: 'en', value: values.en, group: values.group });
      });

      const res = await api.put('/cms/translations/batch', { items });
      if (res?.success) {
        showToast('Barcha tarjimalar muvaffaqiyatli saqlandi!', 'success');
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const filteredKeys = Object.keys(dict).filter((k) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const d = dict[k];
    return (
      k.toLowerCase().includes(q) ||
      (d.uz && d.uz.toLowerCase().includes(q)) ||
      (d.ru && d.ru.toLowerCase().includes(q)) ||
      (d.en && d.en.toLowerCase().includes(q))
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-serif text-lg font-bold text-slate-900">
            Tillar & Tarjimalar Boshqaruvi
          </h2>
          <p className="text-xs text-slate-500">
            Saytdagi barcha 3 ta til (O‘zbekcha, Ruscha, Inglizcha) matnlari lug‘ati
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <div className="relative w-64">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Lug‘atdan qidirish..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-7 py-1.5 border rounded text-xs bg-white focus:outline-none w-full"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs font-bold"
              >
                ✕
              </button>
            )}
          </div>

          <button
            onClick={handleSaveAll}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 text-xs font-semibold text-[#091322] bg-[#c5a880] hover:bg-[#d4b993] rounded shadow-sm transition-colors"
          >
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? 'Saqlanmoqda...' : 'Tarjimalarni Saqlash'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-2xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="py-3 px-4 w-1/4">Lug‘at Kaliti (Key)</th>
                <th className="py-3 px-4 w-1/4">
                  <span className="inline-block px-1.5 py-0.5 rounded bg-slate-200 text-slate-800 font-mono font-bold text-[9px] mr-1.5">UZ</span>
                  O‘zbekcha
                </th>
                <th className="py-3 px-4 w-1/4">
                  <span className="inline-block px-1.5 py-0.5 rounded bg-slate-200 text-slate-800 font-mono font-bold text-[9px] mr-1.5">RU</span>
                  Русский
                </th>
                <th className="py-3 px-4 w-1/4">
                  <span className="inline-block px-1.5 py-0.5 rounded bg-slate-200 text-slate-800 font-mono font-bold text-[9px] mr-1.5">EN</span>
                  English
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={4} className="text-center py-12 text-slate-400">Yuklanmoqda...</td>
                </tr>
              ) : filteredKeys.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-12 text-slate-400">Hech qanday kalit topilmadi</td>
                </tr>
              ) : (
                filteredKeys.map((k) => (
                  <tr key={k} className="hover:bg-slate-50">
                    <td className="py-3 px-4 font-mono font-medium text-slate-700">
                      {k}
                    </td>
                    <td className="py-2 px-4">
                      <input
                        type="text"
                        value={dict[k]?.uz || ''}
                        onChange={(e) => handleValueChange(k, 'uz', e.target.value)}
                        className="w-full px-2 py-1 border border-slate-200 rounded text-xs focus:border-[#c5a880]"
                      />
                    </td>
                    <td className="py-2 px-4">
                      <input
                        type="text"
                        value={dict[k]?.ru || ''}
                        onChange={(e) => handleValueChange(k, 'ru', e.target.value)}
                        className="w-full px-2 py-1 border border-slate-200 rounded text-xs focus:border-[#c5a880]"
                      />
                    </td>
                    <td className="py-2 px-4">
                      <input
                        type="text"
                        value={dict[k]?.en || ''}
                        onChange={(e) => handleValueChange(k, 'en', e.target.value)}
                        className="w-full px-2 py-1 border border-slate-200 rounded text-xs focus:border-[#c5a880]"
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
